/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Showing a stored date to a person.
 *
 * The wire formats — "2027-03-01T10:00" from a datetime-local input,
 * "2027-03-01 14:30:00" from Postgres — are machine punctuation. An admin
 * reading a review screen should see "1 Mar 2027, 10:00 am".
 *
 * Parsed textually rather than through Date(), which would re-interpret the
 * value in the browser's zone and display a different hour than the one the
 * admin typed.
 *
 * A `timeZone` may be passed when the value comes straight from the database,
 * which stores UTC: the value is turned back into that zone's wall clock first,
 * so a list screen shows the event's own local time rather than UTC. Values
 * that are ALREADY a wall clock — anything read off the form model — are
 * formatted without one, exactly as before.
 */

import { utcToWallClock } from '../../utils/zonedDateTime';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/** {day, month, year, hour, minute} or null when the text is not a timestamp. */
function parts(value) {
  const match = PATTERN.exec(String(value));
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const monthIndex = Number(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  return {
    day: Number(day),
    month: MONTHS[monthIndex],
    year: Number(year),
    hour: Number(hour),
    minute,
  };
}

function clockOf({ hour, minute }) {
  const suffix = hour < 12 ? 'am' : 'pm';
  // 0 and 12 both read as 12 on a 12-hour clock.
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${minute} ${suffix}`;
}

const dateOf = ({ day, month, year }) => `${day} ${month} ${year}`;

/**
 * The wall clock to format. With no zone the value is already one; with a zone
 * it is a stored UTC instant that has to be moved into that zone first.
 */
function inZone(value, timeZone) {
  if (!timeZone || value === null || value === undefined || value === '') return value;
  // Falls back to the value itself when the zone is unusable, so an unknown
  // zone shows the stored digits rather than an empty cell.
  return utcToWallClock(value, timeZone) || value;
}

/**
 * "1 Mar 2027, 10:00 am". Unparseable input is returned as given.
 *
 * `timeZone` is optional: with one, `value` is read as the stored UTC instant
 * and shown in that zone; without one it is taken as already being a wall clock.
 */
export function formatDateTime(value, timeZone) {
  if (value === null || value === undefined || value === '') return '—';
  const piece = parts(inZone(value, timeZone));
  if (!piece) return String(value);
  return `${dateOf(piece)}, ${clockOf(piece)}`;
}

/**
 * "1 Mar 2027, 10:00 am – 2:00 pm" for one day, or both dates when it spans
 * more — repeating the date twice for a morning race reads as an error.
 */
export function formatDateTimeRange(start, end, timeZone) {
  const from = parts(inZone(start, timeZone));
  const to = parts(inZone(end, timeZone));

  if (!from && !to) return formatDateTime(start || end, timeZone);
  if (!to) return formatDateTime(start, timeZone);
  if (!from) return formatDateTime(end, timeZone);

  const sameDay = dateOf(from) === dateOf(to);
  return sameDay
    ? `${dateOf(from)}, ${clockOf(from)} – ${clockOf(to)}`
    : `${dateOf(from)}, ${clockOf(from)} – ${dateOf(to)}, ${clockOf(to)}`;
}
